import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { api } from '@/lib/api';
import {
  clearSession,
  getStoredToken,
  getStoredUser,
  persistSession,
  saveAdminSession,
  getAdminSession,
  clearAdminSession,
  type StoredUsuario,
} from '@/lib/auth-storage';

type AuthContextValue = {
  user: StoredUsuario | null;
  token: string | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isImpersonating: boolean;
  login: (email: string, senha: string) => Promise<{ primeiroAcesso: boolean; perfis: string[] }>;
  logout: () => void;
  impersonate: (token: string, user: StoredUsuario) => void;
  stopImpersonating: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [user, setUser] = useState<StoredUsuario | null>(() => getStoredUser());

  const login = useCallback(async (email: string, senha: string) => {
    const { data } = await api.post<{ accessToken: string; usuario: StoredUsuario; primeiroAcesso: boolean }>(
      '/auth/login',
      { email, senha },
    );
    persistSession(data.accessToken, data.usuario);
    setToken(data.accessToken);
    setUser(data.usuario);
    return { primeiroAcesso: data.primeiroAcesso, perfis: data.usuario.perfis };
  }, []);

  const logout = useCallback(() => {
    clearSession();
    clearAdminSession();
    setToken(null);
    setUser(null);
  }, []);

  const impersonate = useCallback((newToken: string, newUser: StoredUsuario) => {
    saveAdminSession();
    persistSession(newToken, newUser);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const stopImpersonating = useCallback(() => {
    const admin = getAdminSession();
    if (!admin) return;
    persistSession(admin.token, admin.user);
    clearAdminSession();
    setToken(admin.token);
    setUser(admin.user);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      isSuperAdmin: user?.perfis?.includes('SUPERADMIN') ?? false,
      isImpersonating: Boolean(user?.impersonatedBy),
      login,
      logout,
      impersonate,
      stopImpersonating,
    }),
    [user, token, login, logout, impersonate, stopImpersonating],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
